import Api from '@/api'
import Utils from '@/utils/utils'
/* global Paddle */

class PaddleService {
  constructor () {
    this.initialized = false
    this.eventCallbacks = []
  }

  initialize () {
    if (this.initialized) return

    if (process.env.NODE_ENV === 'development') {
      console.log('development mode')
      Paddle.Environment.set('sandbox')
    }

    Paddle.Initialize({
      token: Api.paddle_client_token,
      eventCallback: (event) => {
        this.eventCallbacks.forEach(callback => callback(event))
      }
    })

    this.initialized = true
  }

  addEventCallback (callback) {
    this.eventCallbacks.push(callback)
    return () => {
      this.eventCallbacks = this.eventCallbacks.filter(cb => cb !== callback)
    }
  }

  async getPricePreviews ({ customerId, addressId, businessId } = {}) {
    try {
      const request = { items: [] }
      request.items = [
        {
          quantity: 1,
          priceId: Api.paddle_plans['st-pro'].year
        },
        {
          quantity: 1,
          priceId: Api.paddle_plans['st-pro'].month
        }
      ]

      // Add daily plan if available, staging only
      if (Api.paddle_plans['st-pro'].day) {
        request.items.push({
          quantity: 1,
          priceId: Api.paddle_plans['st-pro'].day
        })
      }

      if (customerId) {
        request.customerId = customerId
      }
      if (addressId) {
        request.addressId = addressId
      }
      if (businessId) {
        request.businessId = businessId
      }

      const result = await Paddle.PricePreview(request)
      return this.formatPriceResults(result)
    } catch (error) {
      console.error(`Error fetching prices: ${error.message}`)
      throw error
    }
  }

  formatPriceResults (result) {
    const prices = {
      'st-pro': { month: null, year: null }
    }

    const loadedPlans = {
      'st-pro': {}
    }

    result.data.details.lineItems.forEach((item) => {
      const price = item.formattedTotals.subtotal
      if (item.price.id === Api.paddle_plans['st-pro'].month) {
        prices['st-pro'].month = price
        loadedPlans['st-pro'].month = item
      } else if (item.price.id === Api.paddle_plans['st-pro'].year) {
        prices['st-pro'].year = price
        loadedPlans['st-pro'].year = item
      } else if (item.price.id === Api.paddle_plans['st-pro'].day) {
        prices['st-pro'].day = price
        loadedPlans['st-pro'].day = item
      }
    })

    return { prices, loadedPlans, currencyCode: result.data.currencyCode }
  }

  detectCurrency (item) {
    return item.formattedTotals.subtotal.indexOf('€') > -1 ? 'EUR' : 'USD'
  }

  calculateYearlySavings (loadedPlan, currencyCode) {
    if (!loadedPlan.year || !loadedPlan.month) return null

    const savings = (loadedPlan.month.totals.subtotal * 12) - loadedPlan.year.totals.subtotal
    const currency = currencyCode || this.detectCurrency(loadedPlan.month)

    const locale = navigator.language || navigator.userLanguage
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(savings / 100)
  }

  /**
   * One formatting path for every price shown on the Plans page, so the
   * monthly price, the yearly per-month equivalent and the annual total all
   * follow the same locale rules (symbol position, separators).
   * @param {number} amount   major units, e.g. 187.5
   * @param {string} currency ISO 4217 code from the Paddle preview
   * @param {string} locale   BCP 47 tag, e.g. this.$i18n.locale
   */
  formatCurrency (amount, currency, locale) {
    const options = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
      currencyDisplay: 'narrowSymbol'
    }

    try {
      return Utils.formatNumber(amount, locale, options)
    } catch (error) {
      // Engines older than ES2020 reject currencyDisplay: 'narrowSymbol'.
      delete options.currencyDisplay
      return Utils.formatNumber(amount, locale, options)
    }
  }

  /** Formatted subtotal of one Paddle price-preview line item, or null when it is not loaded. */
  formatLineItemTotal (item, currencyCode, locale) {
    if (!item) return null

    const currency = currencyCode || this.detectCurrency(item)
    return this.formatCurrency(Number(item.totals.subtotal) / 100, currency, locale)
  }

  calculateMonthlyEquivalent (loadedPlan, currencyCode, locale) {
    if (!loadedPlan?.year) return null

    const perMonth = Number(loadedPlan.year.totals.subtotal) / 12
    const currency = currencyCode || this.detectCurrency(loadedPlan.year)

    return this.formatCurrency(perMonth / 100, currency, locale)
  }
}

export default new PaddleService()
